import { Component, Input, OnInit } from '@angular/core';
import { ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, IDatasource, IGetRowsParams } from 'ag-grid-community';
import { MassUploadGridSubmissionDataService } from '../services/mug-submission-data.service';
import { EnrollmentResult, FullSubmissionLogResponse, SubmittedApplication, SubmittedApplicationGridRow, SubmittedEnrollmentsResponse } from '../interfaces/mug-submitted-enrollments.interface';
import { ReducerUtilityService } from '../../../../shared/utility-services/reducer-utility.service';
import { BaseAgency, ENTITLEMENTS, EntitlementsHelperService, LINE_OF_BUSINESS_ENUM, LOGIN_INFO_REDUCER_NAME, USER_ROLE, UserInfoStateStructure } from '@ptl/account-management';
import { REDUCER_TYPES } from '@ptl/state';
import { MassUploadGridCaseService } from '../services/mug-case.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PtlWindowLocationService } from '@ptl/global-components';
import { AgActionMenuCellRendererComponent } from 'src/app/angular-elements/shared/components/ag-grid-cell-renderers/ag-action-menu-cell-renderer/ag-action-menu-cell-renderer.component';
import { ActionItem, ActionItemRendererParams } from 'src/app/angular-elements/shared/components/ag-grid-cell-renderers/interfaces/ag-grid-cell-renderer.interface';
import { DateService } from '../../../../shared/services/date.service';
@Component({
  selector: 'app-mass-upload-grid-submission-log',
  templateUrl: './mass-upload-grid-submission-log.component.html',
  styleUrls: ['./mass-upload-grid-submission-log.component.scss'],
  standalone: false
})
export class MassUploadGridSubmissionLogComponent implements OnInit {
  @Input() massUploadGridUrlThisYear: string = '/individual/tools/mug/enrollment-grid';
  @Input() massUploadGridUrlNextYear: string = '/individual/tools/mug/enrollment-grid-2027';

  hasEntitlement = false;
  isLoading = true;
  errorMessage: string = null;
  caseId: string = null;
  loginId: string = null;
  agency: BaseAgency;

  gridApi: GridApi | null = null;
  columnDefs: ColDef[] = [];
  singleCaseRowData: SubmittedApplicationGridRow[] = [];

  // Pagination settings
  paginationPageSize = 10;
  cacheBlockSize = 10;

  // Grid options
  gridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: this.paginationPageSize,
    paginationPageSizeSelector: [10, 20, 50],
    cacheBlockSize: this.cacheBlockSize,
    rowModelType: 'infinite',
    maxBlocksInCache: 10
  };

  // Track current page
  currentPage = 0;
  totalPages = 0;
  totalElements = 0;

  // Track sorting and filtering
  currentSortBy: string | null = null;
  currentSortDirection: 'ASC' | 'DESC' | null = null;
  currentFilters: { [key: string]: string } = {};

  constructor(
    private mugSubmissionDataService: MassUploadGridSubmissionDataService,
    private reducerUtilityService: ReducerUtilityService,
    private entitlementsHelperService: EntitlementsHelperService,
    private mugCaseService: MassUploadGridCaseService,
    private ptlWindowLocationService: PtlWindowLocationService,
    private matSnackBar: MatSnackBar,
    private dateService: DateService
  ) {}

  async ngOnInit() {
    const loginData: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const hasMassEnrollEntitlement = await this.entitlementsHelperService.entitlementValueForSelectedAgencyByNameAndLob(
      ENTITLEMENTS.MASS_UPLOAD,
      LINE_OF_BUSINESS_ENUM.INDIVIDUAL,
      loginData?.userInfo?.role as USER_ROLE
    );

    if (hasMassEnrollEntitlement) {
      this.hasEntitlement = true;
    } else {
      this.errorMessage = 'Your currently selected agency does not have access to view Mass Upload. Try a different agency if available.';
      return;
    }

    const USER_INFO_STATE_NAME = `${REDUCER_TYPES.APPLICATION_STATE}.${LOGIN_INFO_REDUCER_NAME}`;
    const userInfoState: UserInfoStateStructure = this.reducerUtilityService.getGlobalStateItem(USER_INFO_STATE_NAME);
    this.loginId = userInfoState.userId;

    this.agency = this.entitlementsHelperService.getSelectedAgency(LINE_OF_BUSINESS_ENUM.INDIVIDUAL);

    const url = new URL(window.location.href);
    this.caseId = url.searchParams.get('caseId');

    if (this.caseId) {
      this.loadSubmissionLogsForCase();
    }
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    this.generateColumnDefs();

    // Case ID indicates a single case is being loaded all at once, we do not need to use the server-side model
    if (this.caseId) {
      return;
    }

    const datasource: IDatasource = {
      getRows: (params: IGetRowsParams) => {
        this.loadAllSubmissionLogs(params);
      }
    };

    this.gridApi.addEventListener('sortChanged', () => this.onSortChanged());
    this.gridApi.addEventListener('filterChanged', () => this.onFilterChanged());

    this.gridApi.setGridOption('datasource', datasource);
  }

  async loadSubmissionLogsForCase(): Promise<void> {
    this.isLoading = true;

    try {
      const response: FullSubmissionLogResponse = await this.mugCaseService.fetchCaseSubmissionLogData(this.caseId, this.agency.agencyId);
      this.singleCaseRowData = this.mapContentToGridRows(response.results);
    } 
    catch (error) {
      console.error('Error loading submitted enrollments:', error);
    }

    this.isLoading = false;
  }

  private async loadAllSubmissionLogs(params: IGetRowsParams): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;

    try {
      const pageNumber = Math.floor(params.startRow / this.paginationPageSize);
      let queryParams = { page: pageNumber };

      if (this.currentSortBy) {
        queryParams['sortBy'] = this.currentSortBy;
        if (this.currentSortDirection) {
          queryParams['sortDirection'] = this.currentSortDirection;
        }
      }

      Object.keys(this.currentFilters).forEach(key => {
        queryParams[key] = this.currentFilters[key];
      });

      const response = await this.mugCaseService.fetchAllSubmissionLogData(this.agency.agencyId, queryParams);
      if (!response?.results) {
        return;
      }

      this.currentPage = response.metaData?.currentPage;
      this.totalPages = response.metaData?.totalPages;
      this.totalElements = response.metaData?.totalItems;

      params.successCallback(this.mapContentToGridRows(response.results), this.totalElements);

    } 
    catch (error) {
      console.error('Error loading submission log:', error);
      this.errorMessage = 'Failed to load submission log. Please try again later.';
      params.failCallback();
    } 
    finally {
      this.isLoading = false;
    }
  }

  private mapContentToGridRows(results: EnrollmentResult[]): SubmittedApplicationGridRow[] {
    let allApps = [];

    results.forEach((caseResponse) => {
      allApps.push(this.mapApplicationToGridRows(caseResponse));
    });

    return allApps;
  }
  
  private mapApplicationToGridRows(app: EnrollmentResult): SubmittedApplicationGridRow {
    return {
      caseId: app.caseId,
      subscriberName: app.applicantDetails?.subscriberName,
      applicationNumber: app.appTrackingId,
      numberOfDependents: app.applicantDetails?.dependentCount,
      numberOfSpouses: app.applicantDetails?.spouseCount
    };
  }

  private onSortChanged(): void {
    if (!this.gridApi) return;

    const columnState = this.gridApi.getColumnState();
    this.currentSortBy = null;
    this.currentSortDirection = null;

    if (columnState) {
      const sortedColumn = columnState.find(col => col.sort);
      if (sortedColumn) {
        this.currentSortBy = sortedColumn.colId;
        this.currentSortDirection = sortedColumn.sort === 'asc' ? 'ASC' : 'DESC';
      }
    }

    this.resetPaginationAndRefresh();
  }

  private onFilterChanged(): void {
    if (!this.gridApi) return;

    const filterModel = this.gridApi.getFilterModel();
    this.currentFilters = {};

    Object.keys(filterModel).forEach(key => {
      const filter = filterModel[key];
      if (filter.filter) {
        this.currentFilters[key] = filter.filter;
      }
    });

    this.resetPaginationAndRefresh();
  }

  private resetPaginationAndRefresh(): void {
    this.currentPage = 0;
  }

  getMenuItems(rowData: ActionItemRendererParams): ActionItem[] {
    if (!rowData?.data) return [];

    return [
      {id: 'download', label: 'Download', callback: (r) => this.downloadAppById(r)},
      {id: 'view', label: 'View Case in Enrollment Grid', callback: (r) => this.goToEnrollmentGrid(r)},
    ];
  }

  private generateColumnDefs(): void {
    const commonColProps: Partial<ColDef> = {
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        showFunctionsInFilter: false,
        maxNumConditions: 1
      },
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 150
    };

    this.columnDefs = [
      {
        field: 'actions',
        headerName: 'Actions',
        cellRenderer: AgActionMenuCellRendererComponent,
        cellRendererParams: {
          getActionItems: (rowData: ActionItemRendererParams) => this.getMenuItems(rowData)
        },
        sortable: false,
        resizable: false,
        filter: false,
        width: 80,
        pinned: 'left'
      },
      {
        ...commonColProps,
        field: 'caseId',
        headerName: 'Case ID'
      },
      {
        ...commonColProps,
        field: 'subscriberName',
        headerName: 'Subscriber Name'
      },
      {
        ...commonColProps,
        field: 'applicationNumber',
        headerName: 'App Tracking ID',
        onCellClicked: (params: any) => {
          if (params.value != null) {
            navigator.clipboard.writeText(params.value.toString()).then(() => {
              this.matSnackBar.open(`Copied ${params.value} to clipboard`, 'OK', {
                duration: 2000,
                panelClass: 'positive-panel-class'
              });
            });
          }
        },
        cellRenderer: (params: ICellRendererParams) => {
          if (params?.value) {
            return `
            <div style="display:flex; align-items:center; cursor:pointer;">
              <span class="material-symbols-outlined">content_copy</span>
              <span style="margin-left: 20px">${params?.value}</span>
            </div>`
          }

          return '';
        }
      },
      {
        field: 'numberOfSpouses',
        headerName: 'Number of Spouses',
        sortable: false,
        filter: false,
        flex: 1,
        minWidth: 150
      },
      {
        field: 'numberOfDependents',
        headerName: 'Number of Dependents',
        sortable: false,
        filter: false,
        flex: 1,
        minWidth: 150
      }
    ];
  }

  downloadAppById(rowData: SubmittedApplicationGridRow): void {
    this.mugSubmissionDataService.downloadLogByAppId(rowData.applicationNumber);
  }

  async goToEnrollmentGrid(rowData: SubmittedApplicationGridRow): Promise<void> {
    let currentYear = this.dateService.getTodayAsMoment().year().toString();
    let effectiveDate = '';
    this.isLoading = true;
    
    try {
      const enrollmentRequestResponse = await this.mugCaseService.loadEnrollmentsForCaseId(rowData.caseId.toString(),  this.agency.agencyId);
      effectiveDate = enrollmentRequestResponse?.enrollments[0]?.effectiveDate;
    }
    catch(e) {}

    if (!effectiveDate || currentYear === effectiveDate.split('/')[2]) {
      this.ptlWindowLocationService.goToUrl(`${this.massUploadGridUrlThisYear}?caseId=${rowData.caseId}`, {newTab: true});
    }
    else {
      this.ptlWindowLocationService.goToUrl(`${this.massUploadGridUrlNextYear}?caseId=${rowData.caseId}`, {newTab: true});
    }

    this.isLoading = false;
  }
}
